import { z } from 'zod';
import type {
  CreateMeerwerkCommand,
  MeerwerkDto,
  PhaseId,
  UpdateMeerwerkCommand,
} from '@/api/generated';
import { useCreateMeerwerk, useUpdateMeerwerk } from '@/api/hooks';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/design-system/primitives/Dialog/Dialog';
import { Form, FormField, FormSubmitButton } from '@/design-system/primitives/Form/Form';
import { Input } from '@/design-system/primitives/Input/Input';
import styles from './MeerwerkDialogs.module.css';

const PHASE_OPTIONS: Array<{ id: PhaseId; label: string }> = [
  { id: 'Voor', label: 'Voorbereiding' },
  { id: 'Ontw', label: 'Ontwerp' },
  { id: 'Verg', label: 'Vergunningen' },
  { id: 'Aanb', label: 'Aanbesteding' },
  { id: 'Ruw', label: 'Ruwbouw' },
  { id: 'Wwd', label: 'Wind-/waterdicht' },
  { id: 'Tech', label: 'Technieken' },
  { id: 'Afw', label: 'Afwerking' },
  { id: 'Opl', label: 'Oplevering' },
  { id: 'Naz', label: 'Nazorg' },
];

// Amount is decimal-as-string on the wire so we keep the EUR cents
// intact end-to-end. The browser <input type="number"> already restricts
// keystrokes; the schema only re-checks structure and a non-zero value
// (allows minderwerk via negative numbers).
const schema = z.object({
  title: z.string().trim().min(2, 'Minstens 2 tekens'),
  amount: z
    .string()
    .min(1, 'Verplicht veld')
    .refine((v) => /^-?\d+([.,]\d{1,2})?$/.test(v.trim()), 'Bedrag in euro (bv. 1200 of -350,50)')
    .refine((v) => Number(v.trim().replace(',', '.')) !== 0, 'Bedrag mag niet 0 zijn'),
  phaseId: z.string().min(1, 'Kies een fase'),
});

type MeerwerkFormValues = z.infer<typeof schema>;

function FieldsBody() {
  return (
    <>
      <FormField<MeerwerkFormValues> name="title" label="Omschrijving" required>
        {(field) => <Input {...field} autoFocus placeholder="Bv. ekstra wapening kelder noord" />}
      </FormField>
      <FormField<MeerwerkFormValues>
        name="amount"
        label="Bedrag (€)"
        hint="Negatief voor minderwerk, bv. -350,50"
        required
      >
        {(field) => (
          <Input
            {...field}
            value={(field.value as string) ?? ''}
            type="text"
            inputMode="decimal"
            placeholder="1200"
          />
        )}
      </FormField>
      <FormField<MeerwerkFormValues> name="phaseId" label="Fase" required>
        {(field) => (
          <select
            {...field}
            className={styles.select}
            value={(field.value as string) ?? ''}
            onChange={(e) => field.onChange(e.target.value)}
          >
            {PHASE_OPTIONS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        )}
      </FormField>
    </>
  );
}

export function MeerwerkCreateDialog({
  open,
  onOpenChange,
  defaultPhaseId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultPhaseId?: PhaseId;
}) {
  const createMutation = useCreateMeerwerk();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Nieuw meerwerk</DialogTitle>
        <DialogDescription>
          Leg het meer- of minderwerk vóór de uitvoering vast. Status start op "Voorgesteld".
        </DialogDescription>
        <DialogBody>
          <Form<MeerwerkFormValues>
            schema={schema}
            defaultValues={{
              title: '',
              amount: '',
              phaseId: defaultPhaseId ?? 'Ruw',
            }}
            onSubmit={async (values) => {
              const body: CreateMeerwerkCommand = {
                title: values.title.trim(),
                amount: values.amount.trim().replace(',', '.'),
                phaseId: values.phaseId,
              };
              await createMutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FieldsBody />
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Toevoegen</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

export function MeerwerkEditDialog({
  open,
  onOpenChange,
  meerwerk,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meerwerk: MeerwerkDto;
}) {
  const updateMutation = useUpdateMeerwerk(meerwerk.id);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Meerwerk bewerken</DialogTitle>
        <DialogDescription>Pas omschrijving, bedrag of fase aan.</DialogDescription>
        <DialogBody>
          <Form<MeerwerkFormValues>
            schema={schema}
            defaultValues={{
              title: meerwerk.title,
              amount: String(meerwerk.amount ?? ''),
              phaseId: meerwerk.phaseId,
            }}
            onSubmit={async (values) => {
              const body: UpdateMeerwerkCommand = {
                title: values.title.trim() || null,
                amount: values.amount.trim().replace(',', '.'),
                phaseId: values.phaseId,
              };
              await updateMutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FieldsBody />
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Opslaan</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
